import { useCallback, useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui'
import { Bot, Loader2, Mail, AlertTriangle, RefreshCw } from 'lucide-react'
import { fetchSetting, saveSetting } from '@/services/settings.service'
import { fetchEmailStats, type EmailStats } from '@/services/email.service'

interface Props {
  onAiStatusChange?: (enabled: boolean) => void
}

function getQuotaColor(used: number, limit: number) {
  const pct = (used / limit) * 100
  if (pct >= 90) return { bar: 'bg-red-500', text: 'text-red-600', label: 'Critico' }
  if (pct >= 70) return { bar: 'bg-yellow-500', text: 'text-yellow-600', label: 'Attenzione' }
  return { bar: 'bg-emerald-500', text: 'text-emerald-600', label: 'OK' }
}

export function ImpostazioniPanel({ onAiStatusChange }: Props) {
  const [aiEnabled, setAiEnabled] = useState(false)
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [emailStats, setEmailStats] = useState<EmailStats | null>(null)
  const [emailStatsLoading, setEmailStatsLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const loadSettings = useCallback(async () => {
    setIsLoading(true)
    try {
      const [aiValue, emailValue] = await Promise.all([
        fetchSetting('ai_assistant_enabled'),
        fetchSetting('email_notifications_enabled'),
      ])
      const aiOn = aiValue === 'true'
      setAiEnabled(aiOn)
      onAiStatusChange?.(aiOn)
      setEmailEnabled(emailValue !== 'false')
    } catch {
      console.error('Errore caricamento impostazioni')
    } finally {
      setIsLoading(false)
    }
  }, [onAiStatusChange])

  const loadEmailStats = useCallback(async () => {
    setEmailStatsLoading(true)
    try {
      const stats = await fetchEmailStats()
      setEmailStats(stats)
    } catch {
      console.error('Errore caricamento statistiche email')
    } finally {
      setEmailStatsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
    loadEmailStats()
  }, [loadSettings, loadEmailStats])

  const toggleAi = async () => {
    setIsSaving(true)
    setMessage(null)
    const newValue = !aiEnabled

    try {
      await saveSetting('ai_assistant_enabled', String(newValue))
      setAiEnabled(newValue)
      onAiStatusChange?.(newValue)
      setMessage({
        type: 'success',
        text: newValue
          ? 'Assistente AI attivato! I visitatori vedranno la chat sul sito.'
          : 'Assistente AI disattivato. La chat non sara\' piu\' visibile.',
      })
    } catch {
      setMessage({ type: 'error', text: 'Errore nel salvataggio. Riprova.' })
    } finally {
      setIsSaving(false)
      setTimeout(() => setMessage(null), 4000)
    }
  }

  const toggleEmail = async () => {
    setIsSaving(true)
    setMessage(null)
    const newValue = !emailEnabled

    try {
      await saveSetting('email_notifications_enabled', String(newValue))
      setEmailEnabled(newValue)
      setMessage({
        type: 'success',
        text: newValue
          ? 'Notifiche email attivate! Riceverai un\'email per ogni nuovo lead.'
          : 'Notifiche email disattivate. Non riceverai email per i nuovi lead.',
      })
    } catch {
      setMessage({ type: 'error', text: 'Errore nel salvataggio. Riprova.' })
    } finally {
      setIsSaving(false)
      setTimeout(() => setMessage(null), 4000)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Assistente AI */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${aiEnabled ? 'bg-emerald-100' : 'bg-gray-100'} transition-colors`}>
              <Bot className={`h-8 w-8 ${aiEnabled ? 'text-emerald-600' : 'text-gray-400'} transition-colors`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Assistente AI</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Chat intelligente che guida i visitatori nella valutazione del loro immobile.
                    L'assistente conosce tutte le zone di Sanremo, i prezzi e i coefficienti.
                  </p>
                </div>
                <button
                  onClick={toggleAi}
                  disabled={isSaving}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                    aiEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={aiEnabled}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      aiEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Status */}
              <div className="mt-3 flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    aiEnabled
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${aiEnabled ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                  {aiEnabled ? 'Attivo — visibile ai visitatori' : 'Disattivato'}
                </span>
                {isSaving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              </div>
            </div>
          </div>

          {/* Info box */}
          {aiEnabled && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <strong>Ricorda:</strong> l'assistente AI e' attualmente attivo sul sito.
              I visitatori possono usare la chat per ricevere valutazioni immobiliari in tempo reale.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notifiche Email */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${emailEnabled ? 'bg-blue-100' : 'bg-gray-100'} transition-colors`}>
              <Mail className={`h-8 w-8 ${emailEnabled ? 'text-blue-600' : 'text-gray-400'} transition-colors`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Notifiche Email</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Ricevi un'email di notifica per ogni nuovo lead acquisito tramite
                    il form di valutazione.
                  </p>
                </div>
                <button
                  onClick={toggleEmail}
                  disabled={isSaving}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                    emailEnabled ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={emailEnabled}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      emailEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Status */}
              <div className="mt-3 flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    emailEnabled
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${emailEnabled ? 'bg-blue-500' : 'bg-gray-400'}`} />
                  {emailEnabled ? 'Attive — ricevi notifica per ogni lead' : 'Disattivate'}
                </span>
                {isSaving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback message */}
      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Report Utilizzo Email */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Utilizzo Email — Piano Free Resend
            </h3>
            <button
              onClick={loadEmailStats}
              disabled={emailStatsLoading}
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${emailStatsLoading ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
          </div>

          {emailStatsLoading && !emailStats ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : emailStats ? (
            <div className="space-y-6">
              {/* Quota giornaliera */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Oggi: {emailStats.sentToday} / {emailStats.dailyLimit}
                  </span>
                  <span className={`text-xs font-medium ${getQuotaColor(emailStats.sentToday, emailStats.dailyLimit).text}`}>
                    {getQuotaColor(emailStats.sentToday, emailStats.dailyLimit).label}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${getQuotaColor(emailStats.sentToday, emailStats.dailyLimit).bar}`}
                    style={{ width: `${Math.min((emailStats.sentToday / emailStats.dailyLimit) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* Quota mensile */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Questo mese: {emailStats.sentThisMonth} / {emailStats.monthlyLimit}
                  </span>
                  <span className={`text-xs font-medium ${getQuotaColor(emailStats.sentThisMonth, emailStats.monthlyLimit).text}`}>
                    {getQuotaColor(emailStats.sentThisMonth, emailStats.monthlyLimit).label}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${getQuotaColor(emailStats.sentThisMonth, emailStats.monthlyLimit).bar}`}
                    style={{ width: `${Math.min((emailStats.sentThisMonth / emailStats.monthlyLimit) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* KPI */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{emailStats.sentThisMonth}</p>
                  <p className="text-xs text-gray-500">Inviate</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {emailStats.monthlyLimit - emailStats.sentThisMonth}
                  </p>
                  <p className="text-xs text-gray-500">Rimanenti</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {emailStats.dailyBreakdown.length > 0
                      ? (emailStats.sentThisMonth / emailStats.dailyBreakdown.length).toFixed(1)
                      : '0'}
                  </p>
                  <p className="text-xs text-gray-500">Media/giorno</p>
                </div>
              </div>

              {/* Warning fallite/bloccate */}
              {(emailStats.failedThisMonth > 0 || emailStats.blockedThisMonth > 0) && (
                <div className="flex gap-4 p-3 bg-amber-50 rounded-lg text-sm">
                  {emailStats.failedThisMonth > 0 && (
                    <span className="text-red-600 inline-flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      {emailStats.failedThisMonth} fallite
                    </span>
                  )}
                  {emailStats.blockedThisMonth > 0 && (
                    <span className="text-amber-600 inline-flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      {emailStats.blockedThisMonth} bloccate (quota)
                    </span>
                  )}
                </div>
              )}

              {/* Breakdown giornaliero */}
              {emailStats.dailyBreakdown.some(d => d.count > 0) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Invii giornalieri</h4>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-gray-600 font-medium">Data</th>
                          <th className="text-right px-3 py-2 text-gray-600 font-medium">Inviate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailStats.dailyBreakdown.slice().reverse().map((day) => (
                          <tr key={day.date} className="border-t border-gray-100">
                            <td className="px-3 py-1.5 text-gray-700">
                              {new Date(day.date + 'T00:00:00').toLocaleDateString('it-IT', {
                                weekday: 'short',
                                day: '2-digit',
                                month: 'short',
                              })}
                            </td>
                            <td className="px-3 py-1.5 text-right font-medium text-gray-900">
                              {day.count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Impossibile caricare le statistiche email.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
